import moment from 'moment'
import React, { Component, lazy, Suspense } from 'react'
import { connect } from 'react-redux'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { bindActionCreators, Dispatch } from 'redux'

import Loading from '../../../../components/loading'
import { IApplicationState } from '../../../../store'
import ActivityLevelClassification from '../../../../store/application/models/activity/activity.level.classification'
import Sleep from '../../../../store/application/models/activity/sleep/sleep'
import MedicalIndicator from '../../../../store/application/models/medical.indicator/medical.indicator'
import Report from '../../../../store/application/models/report/report'
import IntradayHeartRate from '../../../../store/application/models/time.series/intraday/heart.rate/intraday.heart.rate'
import TimeSeries from '../../../../store/application/models/time.series/time.series'
import Patient from '../../../../store/application/models/users/patient'
import { TypesUser } from '../../../../store/application/models/users/users'
import { NocturnalUrinaryFrequency } from '../../../../store/application/utils/nocturnal.urinary.frequency'
import * as PatientActions from '../../../../store/ducks/patient/actions'
import * as AcitivityActions from '../../../../store/ducks/patient/activity/actions'
import * as ReportActions from '../../../../store/ducks/patient/report/actions'
import { ISleepPrevious, ITimeSeriesPrevious } from '../../../../store/ducks/patient/report/types'
import * as SleepAction from '../../../../store/ducks/patient/sleep/actions'
import { ILastMeasurementsData } from '../../../../store/ducks/patient/types'
import * as UserActions from '../../../../store/ducks/user/actions'

const FunctionalClinicalReport = lazy(() => import('../../../../components/pdf/functional.clinical.report'))

interface IProps extends RouteComponentProps<any> {
    readonly viewBmi: boolean
    readonly viewWaistCircumference: boolean
    readonly viewCalfCircumference: boolean
    readonly viewHandGrid: boolean
    readonly viewGaitSpeed: boolean
    readonly viewNocturia: boolean
    readonly viewDepression: boolean
    readonly viewSleepiness: boolean
    readonly viewFragility: boolean
    readonly viewSarcopenia: boolean
    readonly viewMuscleStrength: boolean
    readonly viewSteps: boolean
    readonly viewActiveMinutes: boolean
    readonly viewHeartRate: boolean
    readonly viewSleep: boolean
    readonly viewSignature: boolean
    readonly comorbidities: MedicalIndicator
    readonly comorbiditiesLoading: boolean
    readonly medicaments: MedicalIndicator
    readonly medicamentsLoading: boolean
    readonly depression: string
    readonly sleepiness: string
    readonly sarcopenia: string
    readonly nocturiaPresence: string
    readonly frailtySyndrome: string
    readonly muscleStrength: string
    readonly startDate: Date | undefined
    readonly endDate: Date | undefined
    readonly loading: boolean
    readonly user: Patient
    readonly userSuccess: boolean
    readonly userError: boolean
    readonly measurements: ILastMeasurementsData
    readonly steps: TimeSeries
    readonly stepsSuccess: boolean
    readonly stepsError: boolean
    readonly prevSteps: ITimeSeriesPrevious
    readonly prevStepsSuccess: boolean
    readonly prevStepsError: boolean
    readonly activeMinutes: TimeSeries
    readonly activeMinutesSuccess: boolean
    readonly activeMinutesError: boolean
    readonly prevActiveMinutes: ITimeSeriesPrevious
    readonly prevActiveMinutesSuccess: boolean
    readonly prevActiveMinutesError: boolean
    readonly heartRate: IntradayHeartRate[]
    readonly heartRateSuccess: boolean
    readonly heartRateError: boolean
    readonly prevHeartRate: ITimeSeriesPrevious
    readonly prevHeartRateSuccess: boolean
    readonly prevHeartRateError: boolean
    readonly sleeps: Sleep[]
    readonly sleepsSuccess: boolean
    readonly sleepsError: boolean
    readonly activityLevelClassification: ActivityLevelClassification[]
    readonly activityLevelClassificationSuccess: boolean
    readonly activityLevelClassificationLoading: boolean
    readonly activityLevelClassificationError: boolean
    readonly prevSleeps: ISleepPrevious
    readonly prevSleepsSuccess: boolean
    readonly prevSleepsError: boolean

    readonly comparatorReport: Report | undefined
    readonly reportLoading: boolean

    findRequest(id: string, userType: TypesUser): void

    resetCreate(): void

    resetLastMeasurement(): void

    loadLastMeasurementRequest(patientId: string): void

    reset(): void

    resetSleep(): void

    loadStepsRequest(patientId: string, startDate: Date, endDate: Date, type: string): void

    loadActiveMinutesRequest(patientId: string, startDate: Date, endDate: Date, type: string): void

    loadHeartRateRequest(patientId: string, startDate: Date, endDate: Date, type: string): void

    loadSleepRequest(patientId: string, startDate: Date, endDate: Date, type: string): void
    // loadSleepRequest(patientId: string, paginator?: IPaginator, filter?: TimeSeriesSimpleFilter): void

    changeReportView(field: string, value: boolean): void

    loadActivityLevelClassificationRequest(patientId: string, startDate: string, endDate: string): void

    loadMedicalIndicatorsRequest(patientId: string): void

    createReportRequest(report: Report): void
}
export enum Type {
    CURRENT = "current",
    PREVIOUS = "previous"
}

class ReportsComponent extends Component<IProps> {

    constructor(props: IProps) {
        super(props)
        this.loadData = this.loadData.bind(this)
    }

    public componentDidMount() {
        this.loadData()
    }

    public componentWillUnmount() {
        const {
            resetCreate,
            resetLastMeasurement,
            reset,
            resetSleep
        } = this.props
        resetCreate()
        resetLastMeasurement()
        reset()
        resetSleep()
    }

    public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<{}>, snapshot?: any) {
        const { user } = this.props
        const { user: prevUser } = prevProps
        if (user?.id !== prevUser?.id) {
            this.loadData()
        }
    }

    public render() {
        const {
            user,
            userSuccess,
            userError,
            measurements,
            loading,
            steps,
            stepsSuccess,
            stepsError,
            prevSteps,
            prevStepsSuccess,
            prevStepsError,
            activeMinutes,
            activeMinutesSuccess,
            activeMinutesError,
            prevActiveMinutes,
            prevActiveMinutesSuccess,
            prevActiveMinutesError,
            heartRate,
            heartRateSuccess,
            heartRateError,
            prevHeartRate,
            prevHeartRateSuccess,
            prevHeartRateError,
            startDate,
            endDate,
            comparatorReport,
            reportLoading,
            sleeps,
            sleepsSuccess,
            sleepsError,
            prevSleeps,
            prevSleepsSuccess,
            prevSleepsError,
            depression,
            sleepiness,
            sarcopenia,
            nocturiaPresence,
            muscleStrength,
            match: { params },
            viewSleepiness,
            viewSarcopenia,
            viewNocturia,
            viewHeartRate,
            viewMuscleStrength,
            viewDepression,
            viewActiveMinutes,
            viewBmi,
            viewCalfCircumference,
            viewFragility,
            viewSleep,
            viewSteps,
            viewGaitSpeed,
            viewHandGrid,
            viewSignature,
            viewWaistCircumference,
            changeReportView,
            createReportRequest,
            activityLevelClassification,
            activityLevelClassificationSuccess,
            activityLevelClassificationError,
            comorbidities,
            comorbiditiesLoading,
            medicaments,
            medicamentsLoading
        } = this.props

        if (!startDate && !endDate) {
            return <Redirect to={`/app/patients/${params.userId}/report`} />
        }

        const reverse: Sleep[] = [...sleeps].reverse()

        return <Suspense fallback={<Loading />}>
            <FunctionalClinicalReport
                viewSleepiness={viewSleepiness}
                viewSarcopenia={viewSarcopenia}
                viewNocturia={viewNocturia}
                viewHeartRate={viewHeartRate}
                viewMuscleStrength={viewMuscleStrength}
                viewDepression={viewDepression}
                viewActiveMinutes={viewActiveMinutes}
                viewBmi={viewBmi}
                viewCalfCircumference={viewCalfCircumference}
                viewFragility={viewFragility}
                viewSleep={viewSleep}
                viewSteps={viewSteps}
                viewGaitSpeed={viewGaitSpeed}
                viewHandGrid={viewHandGrid}
                viewSignature={viewSignature}
                viewWaistCircumference={viewWaistCircumference}
                changeReportView={changeReportView}
                comorbidities={comorbidities}
                comorbiditiesLoading={comorbiditiesLoading}
                medicaments={medicaments}
                medicamentsLoading={medicamentsLoading}
                depressionPresence={depression ? parseFloat(depression) : undefined}
                sleepiness={sleepiness ? parseFloat(sleepiness) : undefined}
                sarcopeniaPresence={sarcopenia ? parseFloat(sarcopenia) : undefined}
                nocturiaPresence={nocturiaPresence ? NocturnalUrinaryFrequency[nocturiaPresence] : undefined}
                muscleStrength={muscleStrength ? parseFloat(muscleStrength) : undefined}
                startDate={startDate || new Date()}
                endDate={endDate || new Date()}
                user={user}
                userSuccess={userSuccess}
                userError={userError}
                measurements={measurements}
                loading={loading}
                steps={steps}
                stepsSuccess={stepsSuccess}
                stepsError={stepsError}
                prevSteps={prevSteps}
                prevStepsSuccess={prevStepsSuccess}
                prevStepsError={prevStepsError}
                activeMinutes={activeMinutes}
                activeMinutesSuccess={activeMinutesSuccess}
                activeMinutesError={activeMinutesError}
                prevActiveMinutes={prevActiveMinutes}
                prevActiveMinutesSuccess={prevActiveMinutesSuccess}
                prevActiveMinutesError={prevActiveMinutesError}
                heartRate={heartRate}
                heartRateSuccess={heartRateSuccess}
                heartRateError={heartRateError}
                prevHeartRate={prevHeartRate}
                prevHeartRateSuccess={prevHeartRateSuccess}
                prevHeartRateError={prevHeartRateError}
                sleeps={reverse}
                sleepsSuccess={sleepsSuccess}
                sleepsError={sleepsError}
                activityLevelClassification={activityLevelClassification}
                activityLevelClassificationSuccess={activityLevelClassificationSuccess}
                activityLevelClassificationError={activityLevelClassificationError}
                prevSleeps={prevSleeps}
                prevSleepsSuccess={prevSleepsSuccess}
                prevSleepsError={prevSleepsError}
                comparatorReport={comparatorReport}
                reportLoading={reportLoading}
                createReportRequest={createReportRequest} />
        </Suspense>
    }

    private loadData(): void {
        const {
            user,
            match: { params },
            findRequest,
            loadStepsRequest,
            loadActiveMinutesRequest,
            loadLastMeasurementRequest,
            loadHeartRateRequest,
            loadSleepRequest,
            loadActivityLevelClassificationRequest,
            startDate,
            endDate,
            comparatorReport,
            history,
            loadMedicalIndicatorsRequest
        } = this.props
        const userId = params.userId

        if (!startDate && !endDate) {
            history.push(`/app/patients/${params.userId}/report`)
        } else {
            if (!user?.id || user.id !== userId) {
                findRequest(userId, TypesUser.PATIENT)
            }
            loadMedicalIndicatorsRequest(userId)
            loadLastMeasurementRequest(userId)
            loadStepsRequest(userId, startDate || new Date(), endDate || new Date(), Type.CURRENT)
            loadActiveMinutesRequest(userId, startDate || new Date(), endDate || new Date(), Type.CURRENT)
            loadHeartRateRequest(userId, startDate || new Date(), endDate || new Date(), Type.CURRENT)

            loadSleepRequest(userId, startDate || new Date(), endDate || new Date(), Type.CURRENT)

            loadActivityLevelClassificationRequest(
                userId,
                moment(startDate || '').startOf('week').format('YYYY-MM-DD'),
                moment(endDate || '').startOf('week').format('YYYY-MM-DD')
            )

            if (comparatorReport?.start_date !== undefined && comparatorReport?.end_date !== undefined) {
                const comparatorStartDate = comparatorReport?.start_date ? new Date(comparatorReport?.start_date) : new Date()
                const comparatorEndDate = comparatorReport?.end_date ? new Date(comparatorReport?.end_date) : new Date()

                loadStepsRequest(userId, comparatorStartDate, comparatorEndDate, Type.PREVIOUS)
                loadActiveMinutesRequest(userId, comparatorStartDate, comparatorEndDate, Type.PREVIOUS)
                loadHeartRateRequest(userId, comparatorStartDate, comparatorEndDate, Type.PREVIOUS)
                loadSleepRequest(userId, comparatorStartDate, comparatorEndDate, Type.PREVIOUS)

            }
        }
    }
}

const mapStateToProps = (state: IApplicationState) => ({
    comorbidities: state.report.comorbidities.data,
    comorbiditiesLoading: state.report.comorbidities.loading,
    medicaments: state.report.medicaments.data,
    medicamentsLoading: state.report.medicaments.loading,
    depression: state.report.depression,
    sleepiness: state.report.sleepiness,
    sarcopenia: state.report.sarcopenia,
    nocturiaPresence: state.report.nocturiaPresence,
    frailtySyndrome: state.report.frailtySyndrome,
    muscleStrength: state.report.muscleStrength,
    startDate: state.report.startDate,
    endDate: state.report.endDate,
    user: state.user.create.user,
    userSuccess: state.user.create.success,
    userError: state.user.create.error,
    measurements: state.patient.lastMeasurement,
    loading: state.user.create.loading,
    steps: state.report.steps.data,
    stepsSuccess: state.report.steps.success,
    stepsError: state.report.steps.error,
    prevSteps: state.report.prevSteps,
    prevStepsSuccess: state.report.prevSteps.success,
    prevStepsError: state.report.prevSteps.error,
    activeMinutes: state.report.activeMinutes.data,
    activeMinutesSuccess: state.report.activeMinutes.success,
    activeMinutesError: state.report.activeMinutes.error,
    prevActiveMinutes: state.report.prevActiveMinutes,
    prevActiveMinutesSuccess: state.report.prevActiveMinutes.success,
    prevActiveMinutesError: state.report.prevActiveMinutes.error,
    heartRate: state.report.heartRate.data,
    heartRateSuccess: state.report.heartRate.success,
    heartRateError: state.report.heartRate.error,
    prevHeartRate: state.report.prevHeartRate,
    prevHeartRateSuccess: state.report.prevHeartRate.success,
    prevHeartRateError: state.report.prevHeartRate.error,
    sleeps: state.report.sleep.data,
    sleepsSuccess: state.report.sleep.success,
    sleepsError: state.report.sleep.error,
    prevSleeps: state.report.prevSleep,
    prevSleepsSuccess: state.report.prevSleep.success,
    prevSleepsError: state.report.prevSleep.error,

    viewSleepiness: state.report.view.sleepiness,
    viewSarcopenia: state.report.view.sarcopenia,
    viewNocturia: state.report.view.nocturia,
    viewHeartRate: state.report.view.heartRate,
    viewMuscleStrength: state.report.view.muscleStrength,
    viewDepression: state.report.view.depression,
    viewActiveMinutes: state.report.view.activeMinutes,
    viewBmi: state.report.view.bmi,
    viewCalfCircumference: state.report.view.calfCircumference,
    viewFragility: state.report.view.fragility,
    viewSleep: state.report.view.sleep,
    viewSteps: state.report.view.steps,
    viewGaitSpeed: state.report.view.gaitSpeed,
    viewHandGrid: state.report.view.handGrid,
    viewSignature: state.report.view.signature,
    viewWaistCircumference: state.report.view.waistCircumference,

    comparatorReport: state.report.comparatorReport,

    reportLoading: state.report.create.loading,

    activityLevelClassification: state.activities.activityClassification.data,
    activityLevelClassificationLoading: state.activities.activityClassification.loading,
    activityLevelClassificationSuccess: state.activities.activityClassification.success,
    activityLevelClassificationError: state.activities.activityClassification.error
})

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
    {
        ...UserActions,
        ...PatientActions,
        ...ReportActions,
        resetSleep: SleepAction.resetList,
        // loadSleepRequest: SleepAction.loadSleepRequest,
        loadActivityLevelClassificationRequest: AcitivityActions.loadActivityLevelClassificationRequest
    },
    dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(ReportsComponent)
